import { useEffect, useMemo, useRef } from "react"
import { useUserModalContext } from "../../../Contexts/UserModalContext/UserModalProvider"
import { GroupHistoryNoticesInterface } from "../../../Interfaces/ComponentsInterfaces/SharedInterfaces"
import { NoticeInterface } from "../../../Interfaces/Entities"
import ListGroupOfNotices from "../ListData/ListGroupOfNotices"
import { FilterPreviusNotices, FilterThisWeekNotices, FilterTodaysNotices } from "../../../Helpers/FilterFuntions"
import '../SharedComponents.css'

// Matches the backend default in get_news.py when the front sends no date range.
const DEFAULT_HISTORY_WINDOW_DAYS = 30

function isRangeWithinFetchedRange(
	startDate: Date,
	endDate: Date,
	fetchedStart?: Date,
	fetchedEnd?: Date,
): boolean {
	if (!fetchedStart || !fetchedEnd) {
		return false
	}
	return startDate >= fetchedStart && endDate <= fetchedEnd
}

function filterNotices(notices: NoticeInterface[], filters: GroupHistoryNoticesInterface['filters']): NoticeInterface[] {
	let filteredNotices = notices
	if (filters.FilterType.length > 0) {
		filteredNotices = filteredNotices.filter((n) => filters.FilterType.includes(n.type))
	}
	if (filters.FilterGroup.length > 0) {
		filteredNotices = filteredNotices.filter(
			(n) => n.groups.some((g) => filters.FilterGroup.includes(g.id)),
		)
	}
	if (filters.FilterState.length === 1) {
		const stateCode = filters.FilterState[0]
		filteredNotices = stateCode === 'Resolved'
			? filteredNotices.filter((n) => n.solved)
			: filteredNotices.filter((n) => !n.solved)
	}
	if (filters.startDate) {
		const start = new Date(filters.startDate)
		start.setHours(0, 0, 0, 0)
		filteredNotices = filteredNotices.filter((n) => {
			const d = new Date(n.date)
			d.setHours(d.getHours() + 4)
			return d >= start
		})
	}
	if (filters.endDate) {
		const end = new Date(filters.endDate)
		end.setHours(23, 59, 59, 999)
		filteredNotices = filteredNotices.filter((n) => {
			const d = new Date(n.date)
			d.setHours(d.getHours() + 4)
			return d <= end
		})
	}
	return filteredNotices
}

function GroupHistoryNotices({ filters, isApplingFilters, filterActivator }: GroupHistoryNoticesInterface) {

	const { savedNotices, updateNotices, updateNoticesWithDateRange } = useUserModalContext()
	const hasTriedFetch = useRef(false)
	const lastDateRangeFetched = useRef<{ startDate?: Date; endDate?: Date }>({})

	useEffect(() => {
		if ((savedNotices?.length ?? 0) === 0 && !hasTriedFetch.current) {
			hasTriedFetch.current = true
			updateNotices()
			lastDateRangeFetched.current = {}
		}
	}, [savedNotices, updateNotices])

	useEffect(() => {
		if (!isApplingFilters) {
			lastDateRangeFetched.current = {}
			return
		}
		if (!filters.startDate || !filters.endDate) {
			return
		}

		const lastRange = lastDateRangeFetched.current
		const isWithinFetchedRange = isRangeWithinFetchedRange(
			filters.startDate,
			filters.endDate,
			lastRange.startDate,
			lastRange.endDate,
		)
		if (isWithinFetchedRange) {
			return
		}

		let rangeToFetch = {
			startDate: filters.startDate,
			endDate: filters.endDate,
		}
		if (lastRange.startDate && lastRange.endDate) {
			rangeToFetch = {
				startDate: filters.startDate < lastRange.startDate ? filters.startDate : lastRange.startDate,
				endDate: filters.endDate > lastRange.endDate ? filters.endDate : lastRange.endDate,
			}
		}

		updateNoticesWithDateRange(rangeToFetch.startDate, rangeToFetch.endDate)
		lastDateRangeFetched.current = rangeToFetch
	}, [
		isApplingFilters,
		filterActivator,
		filters.startDate,
		filters.endDate,
		updateNoticesWithDateRange,
	])

	// Derived during render so lists never flash empty between a data update and a late useEffect.
	const noticesToShow = useMemo(() => {
		const sourceNotices = savedNotices ?? []
		if (!isApplingFilters) {
			return sourceNotices
		}
		return filterNotices(sourceNotices, filters)
	}, [
		savedNotices,
		isApplingFilters,
		filterActivator,
		filters.FilterType,
		filters.FilterGroup,
		filters.FilterState,
		filters.startDate,
		filters.endDate,
	])

	const todayNotices = useMemo(() => FilterTodaysNotices(noticesToShow), [noticesToShow])
	const thisWeekNotices = useMemo(() => FilterThisWeekNotices(noticesToShow), [noticesToShow])
	const previusNotices = useMemo(() => FilterPreviusNotices(noticesToShow), [noticesToShow])

	const previousSectionTitle = isApplingFilters
		? 'Alertas de semanas anteriores (rango seleccionado)'
		: `Alertas de semanas anteriores (últimos ${DEFAULT_HISTORY_WINDOW_DAYS} días)`

	const previousEmptyMessage = isApplingFilters
		? 'No hay avisos de semanas anteriores en el rango seleccionado'
		: `No hay avisos de semanas anteriores en los últimos ${DEFAULT_HISTORY_WINDOW_DAYS} días`

	return (
		<>
			<ListGroupOfNotices
				noticesList={todayNotices}
				listTitle="Alertas de hoy"
				emptyListMessage="No hay avisos de hoy"
			/>
			<ListGroupOfNotices
				noticesList={thisWeekNotices}
				listTitle="Alertas de esta semana"
				emptyListMessage="No hay avisos de esta semana"
			/>
			<ListGroupOfNotices
				noticesList={previusNotices}
				listTitle={previousSectionTitle}
				emptyListMessage={previousEmptyMessage}
				isLast={true}
			/>
		</>
	)
}

export default GroupHistoryNotices
